<template>
  <b-card no-body v-if="!loading && history && history.cycles && history.cycles.length" class="mb-2">
    <b-card-header class="pb-50">
      <h5 class="mb-0">
        <feather-icon icon="BookOpenIcon" class="mr-50" />
        Manuais do Franqueado
      </h5>
    </b-card-header>
    <b-card-body>
      <!-- Destaque de prazo: evita depender de ler o número de dias. -->
      <b-alert
        v-if="history.current_cycle && deadlineAlert"
        :variant="deadlineAlert.variant"
        show
        class="mb-1"
      >
        <div class="alert-body">
          <feather-icon icon="AlertTriangleIcon" class="mr-50" />
          <span class="font-weight-bold">{{ deadlineAlert.title }}</span>
          <span v-if="history.current_cycle.deadline_label">
            — {{ history.current_cycle.deadline_label }}
          </span>
        </div>
      </b-alert>

      <b-row v-if="history.current_cycle">
        <b-col md="6" class="mb-1" v-if="history.current_cycle.sent_at">
          <small class="text-muted">Enviados em</small>
          <h5>{{ history.current_cycle.sent_at | dateTime }}</h5>
        </b-col>
        <b-col md="6" class="mb-1" v-if="history.current_cycle.receipt_confirmed_at">
          <small class="text-muted">Recebimento confirmado em</small>
          <h5>{{ history.current_cycle.receipt_confirmed_at | dateTime }}</h5>
        </b-col>
        <b-col md="6" class="mb-1" v-if="history.current_cycle.deadline_at">
          <small class="text-muted">Prazo para leitura</small>
          <h5>{{ history.current_cycle.deadline_at | onlyDate }}</h5>
        </b-col>
        <b-col md="6" class="mb-1" v-if="history.current_cycle.deadline_label">
          <small class="text-muted">Prazo restante</small>
          <h5 :class="deadlineTextClass">{{ history.current_cycle.deadline_label }}</h5>
        </b-col>
        <b-col md="6" class="mb-1">
          <small class="text-muted">Status</small>
          <h5>
            <b-badge :variant="statusVariant(history.current_cycle.status)">
              {{ statusLabel(history.current_cycle.status) }}
            </b-badge>
          </h5>
        </b-col>
        <b-col md="6" class="mb-1" v-if="history.current_cycle.reading_confirmed_at">
          <small class="text-muted">Leitura confirmada em</small>
          <h5>{{ history.current_cycle.reading_confirmed_at | dateTime }}</h5>
        </b-col>
      </b-row>

      <hr />

      <p class="font-weight-bold small text-uppercase text-muted mb-1">Histórico por envio</p>

      <div v-for="cycle in history.cycles" :key="cycle.id" class="cycle-block mb-2">
        <div class="d-flex align-items-center flex-wrap mb-75" style="gap:0.5rem">
          <feather-icon icon="SendIcon" size="14" class="text-muted" />
          <strong class="small">
            Envio de {{ cycle.sent_at | dateTime }}
          </strong>
          <b-badge :variant="cycleVariant(cycle)">
            {{ cycleStatusLabel(cycle) }}
          </b-badge>
          <b-badge v-if="!cycle.is_mandatory" variant="light-secondary">Complementar</b-badge>
          <b-badge v-if="cycle.is_mandatory && cycle.is_open" variant="light-primary">Em aberto</b-badge>
        </div>

        <ul class="list-unstyled mb-75 pl-2">
          <li v-for="item in cycle.items" :key="item.manual_id" class="d-flex align-items-start mb-25">
            <feather-icon icon="FileTextIcon" size="14" class="text-muted mr-75 mt-25" />
            <span class="small">
              {{ item.name }} — versão {{ item.version }}
              <b-badge
                v-if="item.is_outdated"
                variant="light-secondary"
                class="ml-25"
                :title="item.current_version ? `Versão vigente: ${item.current_version}` : ''"
              >
                versão antiga
              </b-badge>
              —
              <span v-if="item.reading_confirmed_at" class="text-success">
                {{ cycle.is_mandatory ? 'leitura confirmada em' : 'visualizado em' }}
                {{ item.reading_confirmed_at | dateTime }}
              </span>
              <span v-else class="text-warning">
                {{ cycle.is_mandatory ? 'leitura pendente' : 'ainda não visualizado' }}
              </span>
            </span>
          </li>
        </ul>

        <ul class="list-unstyled mb-0 pl-2">
          <li v-for="(event, i) in cycle.events" :key="i" class="d-flex align-items-start mb-25">
            <feather-icon icon="ClockIcon" size="14" class="text-muted mr-75 mt-25" />
            <span class="small text-muted">
              <strong>{{ event.date | dateTime }}</strong> — {{ event.label }}
            </span>
          </li>
        </ul>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BBadge, BAlert,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import * as types from '../store/types'

const STATUS_LABELS = {
  awaiting_send: 'Aguardando envio',
  send_failed: 'Falha no envio',
  awaiting_receipt: 'Aguardando confirmação de recebimento',
  pending_reading: 'Recebido — leitura pendente',
  near_deadline: 'Prazo próximo do vencimento',
  expired: 'Prazo vencido',
  blocked: 'Acesso bloqueado',
  reading_confirmed: 'Leitura confirmada',
}

const STATUS_VARIANTS = {
  awaiting_send: 'secondary',
  send_failed: 'danger',
  awaiting_receipt: 'info',
  pending_reading: 'warning',
  near_deadline: 'warning',
  expired: 'danger',
  blocked: 'danger',
  reading_confirmed: 'success',
}

export default {
  name: 'FranchiseManualHistoryCard',
  components: {
    BCard, BCardHeader, BCardBody, BRow, BCol, BBadge, BAlert,
  },
  props: {
    userId: {
      type: [Number, String],
      default: undefined,
    },
  },
  data() {
    return {
      loading: false,
      history: undefined,
    }
  },
  computed: {
    deadlineAlert() {
      const status = this.history?.current_cycle?.status
      if (status === 'near_deadline') {
        return { variant: 'warning', title: 'Prazo próximo do vencimento' }
      }
      if (status === 'expired') {
        return { variant: 'danger', title: 'Prazo vencido' }
      }
      if (status === 'blocked') {
        return { variant: 'danger', title: 'Acesso bloqueado por falta de confirmação de leitura' }
      }
      return null
    },
    deadlineTextClass() {
      const status = this.history?.current_cycle?.status
      if (status === 'expired' || status === 'blocked') return 'text-danger'
      if (status === 'near_deadline') return 'text-warning'
      return ''
    },
  },
  watch: {
    userId: {
      immediate: true,
      handler(val) {
        if (val) this.load(val)
      },
    },
  },
  methods: {
    ...mapActions({
      getHistory: types.GET_FRANCHISE_MANUAL_HISTORY,
    }),
    load(userId) {
      this.loading = true
      this.getHistory(userId)
        .then((resp) => {
          this.history = resp.data
        })
        .catch(() => {
          this.history = undefined
        })
        .finally(() => {
          this.loading = false
        })
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status
    },
    // O complementar não tem prazo nem confirmação de leitura, então os
    // rótulos do fluxo obrigatório não se aplicam a ele.
    cycleStatusLabel(cycle) {
      if (cycle.is_mandatory) return this.statusLabel(cycle.status)
      return cycle.reading_confirmed_at ? 'Visualizado' : 'Aguardando visualização'
    },
    cycleVariant(cycle) {
      if (cycle.is_mandatory) return this.statusVariant(cycle.status)
      return cycle.reading_confirmed_at ? 'success' : 'info'
    },
    statusVariant(status) {
      return STATUS_VARIANTS[status] || 'secondary'
    },
  },
}
</script>

<style lang="scss" scoped>
.cycle-block + .cycle-block {
  border-top: 1px solid rgba(128, 128, 128, 0.2);
  padding-top: 0.75rem;
}
</style>
